import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import VisitPersonalInfoCollection from '@/components/AgreementPopup/VisitPersonalInfoCollection';
import DateTimePopup from '../DateTimePopup';
import { useModal } from '@/providers/ModalProvider/ModalProvider';
import { visitConsultGateway } from '@/di-container/remodeling-manager/visit-consult/gateway/client';
import useAuthenticated from '@/hooks/useAuthenticated';
import { usePopup } from '@/providers/PopupProvider/PopupProvider';
import {
  GetVisitConsultChannelInfoResponse,
  ItemCode,
  TimeCode,
  VisitChannelType,
  VisitShopTypeCode,
} from '@remodeling/remodeling-manager';
import { ShopDetail } from '@remodeling/shop';
import ReserveFormHook from './ReserveFormHook';
import { consultGateway } from '@/di-container/remodeling-manager/consult/gateway/client';
import _, { cloneDeep } from 'lodash';
import useSWR from 'swr';
import { shopDetailGateway } from '@/di-container/shop/detail/gateway/client';
import {
  SELECTABLE_DATE_RANGE,
  START_DATE_DIFF,
} from '@/constants/visit-consult';
import { useDataLayer } from '@/hooks/useDataLayer';
import {
  GTM_CST_KEYWORD,
  GTM_CSTC,
  GTM_CSTT,
  GTM_CTA_KEY_NAME,
  GTM_CTA_REMO_EVENT_NAME,
  GTM_KEY_NAME,
  GTM_VISIT_CONSULT_KEYWORD,
  GTM_VISIT_RESERVE_FIN,
} from '@/constants';
import { useBraze } from '@/providers/BrazeProvider';
import { format } from 'date-fns';

interface UseVisitConsultProps {
  shopId: number;
}

const NOT_NUMBER_REGEX = /[^0-9]/g;
const TEL_FORM_REGEX_10 = /^(\d{0,3})(\d{0,3})(\d{0,4})$/g;
const TEL_FORM_REGEX_11 = /^(\d{0,3})(\d{0,4})(\d{0,4})$/g;
const REGEX = /(-{1,2})$/g;
const MAX_SIZE = 10 * 1024 * 1024;

const useVisitConsult = ({ shopId }: UseVisitConsultProps) => {
  const method = ReserveFormHook();
  const router = useRouter();
  const [items, setItems] = useState<ItemCode[]>([]);
  const [times, setTimes] = useState<TimeCode[]>([]);
  const [minDate, setMinDate] = useState<Date>();
  const [maxDate, setMaxDate] = useState<Date>();
  const { handleOpen, handleClose } = usePopup();
  const { handleOpen: modalOpen } = useModal();
  const { handleOpen: acceptModal } = useModal();
  const { handleOpen: sizeLimitModal } = useModal();
  const { handlePushAndRemove } = useDataLayer();
  const { isAuthenticated } = useAuthenticated();
  const isProcessing = useRef(false);
  const channelType = router.query.channelType as VisitChannelType;
  const channelId =
    router.query.channelId === 'undefined'
      ? ''
      : (router.query.channelId as string);
  const channelUrl = router.query.channelUrl as string;
  const returnUrl = router.query.returnUrl as string;

  const { data: shopDetailResponse } = useSWR({ shopId }, () =>
    shopDetailGateway().getDetail.exec({
      shopId,
      typeValue: 'MOBILE',
    }),
  );

  const shopDetail = useMemo(() => {
    return shopDetailResponse?.data as ShopDetail;
  }, [shopDetailResponse]);

  const { data: channelInfoResponse } =
    useSWR<GetVisitConsultChannelInfoResponse>(
      `shop/display-search?channelId=${channelId}`,
      (_) =>
        visitConsultGateway().getChannel.exec({
          channelId,
          channelType,
          channelUrl,
        }),
    );

  const channelInfoData = useMemo(
    () => channelInfoResponse?.data?.channel,
    [channelInfoResponse?.data?.channel],
  );

  const { setValue, getValues, watch } = method;
  const nextImgId = useRef<number>(1);
  const isKindListView = useMemo(() => {
    return (
      shopDetail?.type?.code === 'S01' &&
      !channelId &&
      channelType === 'OP20_01'
    );
  }, [channelId, channelType, shopDetail]);

  const { logCustomEvent } = useBraze();

  const itemTypesFromUrlParams = useMemo(
    () => router.query?.itemTypes,
    [router.query?.itemTypes],
  );

  const handleSubmitData = useCallback(
    (data: any) => {
      const reqData = cloneDeep(data);

      if (isProcessing.current) {
        return;
      }

      isProcessing.current = true;

      visitConsultGateway()
        .request.exec({
          ...reqData,
          visitTimeType: reqData.visitTimeType,
          userName: reqData.userName?.trim(),
          itemType: reqData.itemType || itemTypesFromUrlParams || null,
        })
        .then((data) => {
          if (data.code === 200) {
            handlePushAndRemove({
              data: {
                event: GTM_CTA_REMO_EVENT_NAME,
                [GTM_CTA_KEY_NAME]: GTM_CSTC,
                [GTM_KEY_NAME]: GTM_VISIT_RESERVE_FIN,
                [GTM_CST_KEYWORD]: GTM_VISIT_CONSULT_KEYWORD,
                consult_request_id: data?.data?.visitConsultId,
              },
              key: GTM_KEY_NAME,
            });

            logCustomEvent({
              eventName: 'bz_complete_arrange_visit_store',
              eventProperties: {
                bz_date: format(new Date(), 'yyyy-MM-dd'),
                bz_store_type: _.get(shopDetail, 'type.name', ''),
                bz_store_area: _.get(shopDetail, 'address.basic', ''),
                bz_store_num: _.toString(_.get(shopDetail, 'id', '')),
                bz_store_name: _.get(shopDetail, 'name', ''),
              },
            });

            modalOpen({
              modalProps: {
                hasIcon: false,
                type: 'alert',
                text: '매장 상담 예약이 완료되었습니다.',
                onConfirm: () => {
                  isProcessing.current = false;
                  if (returnUrl) {
                    router.push(`${returnUrl}`);
                  } else {
                    router.push(`/shop/${shopDetail.id}`);
                  }
                },
              },
            });
          }
        })
        .catch(() => {
          isProcessing.current = false;
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      handlePushAndRemove,
      modalOpen,
      returnUrl,
      router,
      shopDetail,
      itemTypesFromUrlParams,
    ],
  );

  const setItemCode = useCallback(async () => {
    await visitConsultGateway()
      .getItemCode.exec({ shopId: `${shopId}` })
      .then((response) => {
        response?.data && setItems(response.data);
      });
  }, [shopId]);

  const setTimeCode = useCallback(async () => {
    await visitConsultGateway()
      .getTimeCode.exec()
      .then((response) => {
        response?.data && setTimes(response.data);
      });
  }, []);

  const handleChangeTelInput = useCallback(
    (value: string) => {
      if (value.length > 13) return;
      const tel = value
        .replace(NOT_NUMBER_REGEX, '')
        .replace(
          value.length < 13 ? TEL_FORM_REGEX_10 : TEL_FORM_REGEX_11,
          '$1-$2-$3',
        )
        .replace(REGEX, '');
      setValue('userTel', tel);
    },
    [setValue],
  );

  const handleChangeName = (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    if (value.trim().length === 0) {
      setValue('userName', value.replace(/^\s*/, '').replace(/\s*$/, ''));
    } else setValue('userName', value);
  };

  const handleSetDateTime = useCallback(
    (e: { date: string; time: any }) => {
      setValue('visitTimeType', e.time.code, { shouldValidate: true });
      setValue('visitDate', e.date, { shouldValidate: true });
    },
    [setValue],
  );

  const [isNameDisabled, setIsNameDisabled] = useState<boolean>(false);
  const [isTelDisabled, setIsTelDisabled] = useState<boolean>(false);

  const setLoggedInUserInfo = useCallback(async () => {
    await visitConsultGateway()
      .getBasic.exec()
      .then((response) => {
        const user = response?.data?.user;
        const userName = user?.name || '';
        const userTel = user?.tel || '';

        if (user?.simpleUserFlag === 'N') {
          setValue('userName', userName, {
            shouldValidate: true,
          });
          setValue('userTel', userTel, {
            shouldValidate: true,
          });

          setIsNameDisabled(true);
          setIsTelDisabled(true);
        }
      });
  }, [setValue]);

  const handlePopupDateTime = useCallback(() => {
    handleOpen({
      popupProps: {
        title: '방문일시 선택',
        buttonType: 'noneBtn',
        gtmName: 'visit_date_popup',
        titleStyle: {
          margin: '16px 28px 0px 28px',
          paddingBottom: '8px',
        },
        wrapStyle: {
          width: '909px',
        },
        children: (
          <DateTimePopup
            times={times}
            currentTime={watch('visitTimeType')}
            currentDate={watch('visitDate')}
            minDate={minDate}
            maxDate={maxDate}
            closeModal={handleClose}
            onChange={handleSetDateTime}
          />
        ),
      },
    });
  }, [
    handleOpen,
    times,
    watch,
    minDate,
    maxDate,
    handleClose,
    handleSetDateTime,
  ]);

  const handleAgreement = useCallback(() => {
    handleOpen({
      popupProps: {
        title: '개인정보 수집 및 이용 동의',
        popupSize: 'xxl',
        gtmName: 'visit_consult_agree_popup',
        children: (
          <VisitPersonalInfoCollection
            isEvent={false}
            isMember={isAuthenticated}
          />
        ),
      },
    });
  }, [handleOpen, isAuthenticated]);
  useEffect(() => {
    setValue('visitShop.shopId', shopDetail?.id, {
      shouldValidate: true,
    });
    setValue('visitShop.shopName', shopDetail?.name, {
      shouldValidate: true,
    });
    setValue(
      'visitShop.shopType',
      shopDetail?.type?.code as VisitShopTypeCode,
      {
        shouldValidate: true,
      },
    );
    setValue('visitShop.shopCode', shopDetail?.shopCode as string, {
      shouldValidate: true,
    });
  }, [setValue, shopDetail]);

  const setDate = useCallback(
    (year: number, month: number, date: number) => {
      const calcMonth = `${month < 10 ? '0' : ''}${month}`;
      const calcDate = `${date < 10 ? '0' : ''}${date}`;

      setValue('visitDate', `${year}-${calcMonth}-${calcDate}`);
    },
    [setValue],
  );

  const setTodayDate = useCallback(() => {
    if (getValues('visitDate')) return;

    const today = new Date();
    const maxDate = new Date(today);
    const minDate = new Date(today);

    minDate.setDate(today.getDate() + START_DATE_DIFF);
    maxDate.setDate(today.getDate() + SELECTABLE_DATE_RANGE + START_DATE_DIFF);

    setMinDate(minDate);
    setMaxDate(maxDate);

    setDate(minDate.getFullYear(), minDate.getMonth() + 1, minDate.getDate());
  }, [getValues, setDate]);

  const isFileValidation = useCallback(
    (file: File): boolean => {
      if (
        !(
          file.type.includes('png') ||
          file.type.includes('gif') ||
          file.type.includes('png') ||
          file.type.includes('jpeg')
        )
      ) {
        acceptModal({
          modalProps: {
            text: 'jpg, gif, png 파일만 업로드 가능합니다.',
          },
        });
        return false;
      }
      if (file.size > MAX_SIZE) {
        sizeLimitModal({
          modalProps: {
            text: '최대 10MB까지 업로드 가능 합니다.',
          },
        });
        return false;
      }
      return true;
    },
    [acceptModal, sizeLimitModal],
  );

  const handleAddPictureList = useCallback(
    async (file: File) => {
      if (!isFileValidation(file)) {
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      const { data: imgData } = await consultGateway().createImg.exec(formData);
      if (!imgData) return;
      const { fileUrl: url } = imgData;
      const prevList = cloneDeep(getValues('images'));
      if (!prevList) return;
      setValue('images', [...prevList, { seq: nextImgId.current++, url }]);
    },
    [getValues, isFileValidation, setValue],
  );

  const handleRemovePictureFile = useCallback(
    (id: number) => {
      setValue(
        'images',
        watch('images')?.filter((item) => item.seq !== id),
      );
    },
    [setValue, watch],
  );

  const isValid = useMemo(() => {
    if (
      !!watch('visitDate') &&
      !!watch('visitTimeType') &&
      watch('agreement.privacy') === 'Y' &&
      !!watch('userName') &&
      !!watch('userTel')
    ) {
      if (isKindListView) {
        if (watch('itemType')) {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    } else {
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    watch('visitDate'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    watch('visitTimeType'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    watch('agreement.privacy'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    watch('itemType'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    watch('userName'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    watch('userTel'),
  ]);

  useEffect(() => {
    isAuthenticated && setLoggedInUserInfo();
    setItemCode();
    setTimeCode();
    setTodayDate();
  }, [
    isAuthenticated,
    setLoggedInUserInfo,
    setItemCode,
    setTimeCode,
    setTodayDate,
  ]);

  useEffect(() => {
    if (channelType) {
      setValue('channel', {
        channelType: channelType as VisitChannelType,
        channelId: (channelId as unknown as number) || null,
        channelUrl: channelUrl as string,
      });
    }
  }, [channelId, channelType, channelUrl, setValue]);

  useEffect(() => {
    handlePushAndRemove({
      data: {
        event: GTM_CTA_REMO_EVENT_NAME,
        [GTM_CTA_KEY_NAME]: GTM_CSTT,
        [GTM_KEY_NAME]: GTM_VISIT_RESERVE_FIN,
        [GTM_CST_KEYWORD]: GTM_VISIT_CONSULT_KEYWORD,
      },
      key: GTM_KEY_NAME,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    method,
    isValid,
    items,
    times,
    isKindListView,
    handleAgreement,
    handleChangeTelInput,
    handlePopupDateTime,
    handleSubmitData,
    handleChangeName,
    handleAddPictureList,
    handleRemovePictureFile,
    shopDetail,
    channelInfoData,
    channelType,
    isNameDisabled,
    isTelDisabled,
  };
};

export default useVisitConsult;
